import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';

import intersection from 'lodash/intersection';
import uniq from 'lodash/uniq';
import without from 'lodash/without';

import { usePrevious } from '@gogame/hooks-ts';

import HierarchyContext from './HierarchyContext';
import hierarchyReducer, {
  START_AREAS_LOADING,
  SET_AREAS_DATA,
  SET_AREAS_ERROR,
  CLEAR_AREAS,
} from './hierarchyReducer';
import { getIn, setIn } from './utils';

export const useHierarchy = ({
  values,
  active = true,
  onNormalize,
  onDataLoad = () => {},
  normalized,
}) => {
  const areaRegistry = useRef({});

  const _valuesKeyRef = useRef(0);
  const _areaLastFetchKeysRef = useRef({});
  const _valuesRef = useRef(null);
  const _state = useRef({});
  const _areasCount = useRef({});

  const valuesKey = useMemo(() => _valuesKeyRef.current++, [values]);
  const [updateQueue, setUpdateQueue] = useState([]);
  const [state, dispatch] = React.useReducer(hierarchyReducer, {});
  const prevValues = usePrevious(active ? values : undefined);
  const loadingCount = useMemo(() => {
    return Object.keys(areaRegistry.current).reduce((result, key) => {
      const loading = getIn(state, `${key}.loading`);
      return loading ? result + 1 : result;
    }, 0);
  }, [state]);

  _valuesRef.current = values;

  _state.current = state;

  const someDependsChanged = React.useCallback(
    (depends = []) => {
      return depends.some((dep) => {
        const currentValue = getIn(values, dep);
        const previousValue = getIn(prevValues, dep);
        return currentValue !== previousValue;
      });
    },
    [values]
  );

  const runSingleAreaFetch = useCallback(
    (a) => {
      const area = areaRegistry.current[a];
      _areaLastFetchKeysRef.current[a] = valuesKey;
      return area.fetch(_valuesRef.current, _state.current);
    },
    [valuesKey]
  );

  const runAreasFetch = async (...areaKeys) => {
    const results = [];
    const errors = [];

    dispatch({
      type: START_AREAS_LOADING,
      payload: areaKeys,
    });

    const requests = areaKeys.map(runSingleAreaFetch);

    for (let i = 0; i < areaKeys.length; i++) {
      const a = areaKeys[i];
      try {
        const data = await requests[i];
        results.push({ value: data, name: a });
      } catch (e) {
        errors.push({ value: e.message, name: a });
      }
    }

    if (errors.length) {
      dispatch({
        type: SET_AREAS_ERROR,
        payload: errors,
      });
    }

    if (results.length) {
      dispatch({
        type: SET_AREAS_DATA,
        payload: results,
      });
    }

    onDataLoad({ results, errors });

    return results;
  };

  const update = useCallback(
    (...areaKeysForUpdate) => {
      if (!active) {
        return;
      }
      let currentUpdateAreaKeys = normalized
        ? areaKeysForUpdate
        : areaKeysForUpdate.filter((a) => {
            const area = areaRegistry.current[a];

            return !intersection(without(areaKeysForUpdate, a), area.depends)
              .length;
          });

      let nextUpdatedAreaKeys = without(
        areaKeysForUpdate,
        ...currentUpdateAreaKeys
      );

      if (!currentUpdateAreaKeys.length && nextUpdatedAreaKeys.length) {
        currentUpdateAreaKeys = nextUpdatedAreaKeys;
        nextUpdatedAreaKeys = [];
      }

      return runAreasFetch(...currentUpdateAreaKeys).then((areasData) => {
        if (onNormalize && !normalized) {
          const changes = {};
          let normalizedValues = areasData.reduce((normalizedValues, data) => {
            const area = areaRegistry.current[data.name];

            if (area && area.normalizeValue) {
              const value = getIn(_valuesRef.current, data.name);
              let normalizedValue = null;
              try {
                normalizedValue = area.normalizeValue(value, data.value);
              } catch (e) {
                normalizedValue = value;
              }

              if (normalizedValue !== value) {
                changes[data.name] = {
                  currentValue: value,
                  normalizedValue,
                  name: data.name,
                  data: data.value,
                };
                return setIn(normalizedValues, data.name, normalizedValue);
              }
            }
            return normalizedValues;
          }, _valuesRef.current);

          if (normalizedValues !== _valuesRef.current) {
            const finished = nextUpdatedAreaKeys.length === 0;
            onNormalize(
              { normalizedValues, currentValues: _valuesRef.current, changes },
              finished
            );
          }
        }

        if (nextUpdatedAreaKeys.length) {
          setUpdateQueue((queue) => {
            return uniq([...queue, ...nextUpdatedAreaKeys]);
          });
        }
      });
    },
    [active, onNormalize, normalized, runAreasFetch]
  );

  const registerArea = React.useCallback((name, area) => {
    areaRegistry.current[name] = area;

    if (!_areasCount.current[name]) {
      _areasCount.current[name] = 0;
      setUpdateQueue((queue) => uniq([...queue, name]));
    }
    _areasCount.current[name]++;
  }, []);

  const unregisterArea = React.useCallback((name) => {
    _areasCount.current[name]--;
    if (!_areasCount.current[name]) {
      delete areaRegistry.current[name];
      delete _areaLastFetchKeysRef.current[name];
      dispatch({
        type: CLEAR_AREAS,
        payload: [name],
      });
    }
  }, []);

  useEffect(() => {
    if (active) {
      update(...Object.keys(areaRegistry.current));
    }
  }, [active]);

  useEffect(() => {
    if (updateQueue.length) {
      const areaKeysForUpdate = Object.keys(areaRegistry.current).filter(
        (a) => {
          return (
            _areaLastFetchKeysRef.current[a] !== valuesKey &&
            updateQueue.includes(a)
          );
        }
      );
      update(...areaKeysForUpdate);
      setUpdateQueue((queue) => without(queue, ...updateQueue));
    }
  }, [updateQueue]);

  useEffect(() => {
    if (!prevValues) {
      return;
    }
    const areaKeysForUpdate = Object.keys(areaRegistry.current).filter((a) => {
      const area = areaRegistry.current[a];
      return (
        _areaLastFetchKeysRef.current[a] !== valuesKey &&
        someDependsChanged(area.depends)
      );
    });

    update(...areaKeysForUpdate);
    if (areaKeysForUpdate.length) {
      setUpdateQueue((queue) => without(queue, ...areaKeysForUpdate));
    }
  }, [valuesKey]);

  return useMemo(
    () => ({
      registerArea,
      unregisterArea,
      state,
      update,
      loadingCount,
      active,
    }),
    [registerArea, unregisterArea, state, update, loadingCount, active]
  );
};

const Hierarchy = React.forwardRef(
  ({ children, values, active, onNormalize, onDataLoad, normalized }, _ref) => {
    const {
      registerArea,
      unregisterArea,
      state,
      update,
      loadingCount,
      active: hierarchyActive,
    } = useHierarchy({
      values,
      active,
      onNormalize,
      onDataLoad,
      normalized,
    });
    return (
      <HierarchyContext.Provider
        value={{
          registerArea,
          unregisterArea,
          state,
          update,
          loadingCount,
          active: hierarchyActive,
        }}
      >
        {typeof children === 'function' ? children({ state }) : children}
      </HierarchyContext.Provider>
    );
  }
);

export default Hierarchy;
